import Tooltip from "aldoo-ra/Tooltip"
import { useStuck, getPlanPriceLabel, getValue, ValueCell, resolveHighlight } from "./helpers"

function Group({ group, plans, texts, storeData }) {
  return (
    <>
      <tr className="bg-neutral_1/60 border-b border-bordered/60">
        <td colSpan={plans.length + 1} className="px-4 py-2.5 text-base font-bold text-text tracking-wide">
          {group.category.name}
        </td>
      </tr>

      {group.features.map((feature, fi) => (
        <tr
          key={feature._id || fi}
          className="border-b border-bordered/40"
        >
          <td className="px-4 py-4 text-sm lg:text-base text-text">
            {feature.name}
            {feature.tooltip && (
              <Tooltip text={feature.tooltip} position="top"><span className="ml-1 text-text_secondary cursor-help">ⓘ</span></Tooltip>
            )}
          </td>
          
          {plans.map((plan) => {
            const productId = typeof plan.product === "string" ? plan.product : (plan.product?.item || plan.product?._id)
            const product = storeData?.products?.[productId]
            const rawValue = getValue(feature, plan)
            const resolvedValue = resolveHighlight(rawValue, product)
            return (
              <td key={plan._id} className="px-4 py-2.5 text-center">
                <ValueCell value={resolvedValue} texts={texts} />
              </td>
            )
          })}
        </tr>
      ))}
    </>
  )
}

export default function FeatureTable({ plans, features, categories, texts, storeData, billing }) {
  const [sentinelRef, stuck] = useStuck(64)
  const grouped = categories.map((cat) => {
    const catId = cat._id
    const catFeatures = features.filter((f) => {
      const fCatId = typeof f.category === "string" ? f.category : f.category?._id
      return fCatId === catId
    })
    return { category: cat, features: catFeatures }
  })

  const categorisedIds = new Set(categories.map((c) => c._id))
  const uncategorised = features.filter((f) => {
    const fCatId = typeof f.category === "string" ? f.category : f.category?._id
    return !fCatId || !categorisedIds.has(fCatId)
  })
  if (uncategorised.length > 0) {
    grouped.push({ category: { name: texts?.other }, features: uncategorised })
  }

  return (
    <div className="bg-paper rounded-3xl p-6">
      <div ref={sentinelRef} className="h-0" />
      <div className="rounded-3xl border border-bordered overflow-clip">
        <table className="w-full text-left rounded-3xl">
          <thead className="sticky top-16 z-20 bg-neutral_1 border-b border-bordered">
            <tr>
              <th className={`px-4 py-3 text-base xl:text-lg font-semibold text-text w-1/4 align-bottom ${stuck ? "" : "rounded-tl-2xl"}`}>
                {texts?.features}
              </th>

              {plans.map((plan, pi) => {
                const price = stuck ? getPlanPriceLabel(plan, storeData, billing, texts) : ""
                return (
                  <th key={plan._id} className={`px-4 py-3 text-base xl:text-lg font-semibold text-text text-center align-bottom ${pi === plans.length - 1 && !stuck ? "rounded-tr-2xl" : ""}`}>
                    <span>{plan.name}</span>
                    <span
                      className={`block overflow-hidden transition-all duration-300 ease-out ${stuck && price ? "max-h-6 opacity-100 mt-0.5" : "max-h-0 opacity-0"}`}
                    >
                      <span className="text-xs xl:text-sm font-medium text-text_secondary">{price}</span>
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          
          <tbody>
            {grouped.map(
              (group, gi) =>
                group.features.length > 0 && (
                  <Group key={group.category._id || gi} group={group} plans={plans} texts={texts} storeData={storeData} />
                )
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}